// Vue « Préférences » : ma journée, mes propositions, apparence, données (export, import, corbeille), compte, version.
import { label, type Theme } from '../core/contact';
import { hm, parseTime, prettyDate } from '../core/time';
import { versionLabel, VERSION } from '../core/version';
import { google } from '../data/google';
import { pushStatus, pushSubscribe, pushUnsubscribe, type PushStatus } from '../data/push';
import type { Store } from '../data/store';
import type { MountedView } from './contacts';
import { download, el, esc, fmtClock, on } from './dom';
import { icon } from './icons';
import { statusInfo, syncBanner } from './shell';
import { showToast } from './toast';
import { mountZonePicker, type ZonePicker } from './zonePicker';

const DAY_LABELS = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
const DAY_ORDER = [1, 2, 3, 4, 5, 6, 0];

export function mountSettings(main: HTMLElement, store: Store, ctx: { openImport(): void }): MountedView {
  const p0 = store.state.profile;
  let days = [...p0.days];
  let theme: Theme = p0.theme;

  main.innerHTML = `
    <div data-banner></div>
    <section class="page-head"><div><div class="eyebrow">Réglages</div><h1>Préférences</h1><p>Vos horaires, votre nom pour les clients, vos données.</p></div></section>
    <form class="stack" novalidate data-profile>
      <section class="card">
        <h2>Ma journée</h2><p class="sub">Le fuseau et les horaires qui servent de référence à toute l’application.</p>
        <div class="field"><label for="p-zone">Ma ville</label><div data-zone></div></div>
        <div class="two">
          <div class="field"><label for="p-start">Je commence à</label><input id="p-start" type="time" step="60" required value="${esc(hm(p0.start))}"></div>
          <div class="field"><label for="p-end">Je termine à</label><input id="p-end" type="time" step="60" required value="${esc(hm(p0.end))}"></div>
        </div>
        <div class="field"><label>Mes jours travaillés</label><div class="seg" role="group" aria-label="Jours travaillés" data-days></div></div>
      </section>
      <section class="card">
        <h2>Mes propositions d’appel</h2><p class="sub">Ce que vos clients voient sur la page de proposition (phase 3).</p>
        <div class="two">
          <div class="field"><label for="p-name">Nom affiché aux clients</label><input id="p-name" maxlength="60" value="${esc(p0.displayName)}" placeholder="Rodolphe Melot · Acte 5"></div>
          <div class="field"><label for="p-email">E-mail de réception des confirmations</label><input id="p-email" type="email" maxlength="120" value="${esc(p0.replyEmail)}" placeholder="contact@acte-5.com"></div>
        </div>
      </section>
      <section class="card">
        <h2>Apparence</h2>
        <div class="field"><label>Thème</label><div class="seg" role="group" aria-label="Thème" data-theme-seg>
          <button type="button" data-theme-value="light" aria-pressed="${theme === 'light'}">${icon('sun')}Clair</button>
          <button type="button" data-theme-value="dark" aria-pressed="${theme === 'dark'}">${icon('moon')}Sombre</button></div>
          <small>Le thème est enregistré avec vos préférences : identique sur tous vos appareils.</small></div>
        <p class="error" role="alert" data-error></p>
        <div class="card-actions"><button class="btn primary" type="submit" data-save>Enregistrer mes préférences</button></div>
      </section>
    </form>
    <div class="stack" style="margin-top:18px">
      <section class="card">
        <h2>Google Agenda</h2>
        <p class="sub">Relié, il crée vos événements avec un lien Meet depuis le planificateur, retire vos créneaux occupés de la page de rendez-vous, envoie les invitations aux visiteurs et vous prévient par e-mail de chaque nouveau rendez-vous.</p>
        <div class="hint" data-google-state>Vérification…</div>
        <div class="card-actions"><button class="btn primary" type="button" data-google-connect hidden>${icon('calendar')}Relier Google Agenda</button><button class="btn" type="button" data-google-test-email hidden>${icon('mail')}Envoyer un e-mail de test</button><button class="btn ghost danger" type="button" data-google-disconnect hidden>Déconnecter</button></div>
      </section>
      <section class="card">
        <h2>Notifications sur ce téléphone</h2>
        <p class="sub">En plus de l’e-mail, une notification arrive directement sur cet appareil à chaque nouveau rendez-vous ou annulation — même l’application fermée.</p>
        <div class="hint" data-push-state>Vérification…</div>
        <div class="card-actions"><button class="btn primary" type="button" data-push-on hidden>${icon('bell')}Activer les notifications sur ce téléphone</button><button class="btn ghost danger" type="button" data-push-off hidden>Désactiver</button></div>
      </section>
      <section class="card">
        <h2>Mes données</h2><p class="sub">Vos contacts vivent sur le serveur. Une sauvegarde de votre côté ne fait jamais de mal.</p>
        <div class="card-actions">
          <button class="btn" type="button" data-export>${icon('download')}Exporter mes contacts (JSON)</button>
          <button class="btn" type="button" data-import>${icon('upload')}Importer une sauvegarde</button>
        </div>
        <div data-trash></div>
      </section>
      <section class="card">
        <h2>Compte et version</h2>
        <dl class="kv" data-account></dl>
        <div class="card-actions"><button class="btn" type="button" data-retry>${icon('refresh')}Relire le serveur</button><button class="btn ghost" type="button" data-logout>${icon('logout')}Se déconnecter</button></div>
      </section>
    </div>`;

  const zone: ZonePicker = mountZonePicker(el(main, '[data-zone]'), { id: 'p-zone', initial: { tz: p0.tz, place: p0.place, country: p0.country } });
  const paintDays = () => { el(main, '[data-days]').innerHTML = DAY_ORDER.map((n) => `<button type="button" data-day="${n}" aria-pressed="${days.includes(n)}">${DAY_LABELS[n]}</button>`).join(''); };
  const paintTheme = () => main.querySelectorAll<HTMLElement>('[data-theme-value]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.themeValue === theme)));
  paintDays();

  on(main, 'click', '[data-day]', (_e, t) => { const n = Number(t.dataset.day); days = days.includes(n) ? days.filter((x) => x !== n) : [...days, n]; paintDays(); el<HTMLElement>(main, `[data-day="${n}"]`).focus(); });
  on(main, 'click', '[data-theme-value]', (_e, t) => { theme = t.dataset.themeValue as Theme; paintTheme(); document.documentElement.dataset.theme = theme; });
  async function paintGoogle(): Promise<void> {
    const box = el(main, '[data-google-state]');
    const connectBtn = el<HTMLButtonElement>(main, '[data-google-connect]');
    const disconnectBtn = el<HTMLButtonElement>(main, '[data-google-disconnect]');
    const testEmailBtn = el<HTMLButtonElement>(main, '[data-google-test-email]');
    testEmailBtn.hidden = true;
    try {
      const s = await google.status();
      if (!s.configured) { box.innerHTML = `${icon('alert')} Pas encore configuré côté serveur : l’identifiant et le secret Google sont à poser dans Cloudflare (notice docs/GOOGLE.md).`; connectBtn.hidden = true; disconnectBtn.hidden = true; return; }
      if (s.connected) {
        box.innerHTML = `${icon('check')} Relié : <b>${esc(s.email || 'compte Google')}</b>. Les créneaux occupés de votre agenda ne sont plus proposés, et les invitations partent de votre compte.`
          + (s.canEmail ? '' : `<div style="margin-top:6px">${icon('alert')} L’e-mail de notification n’est pas encore actif sur cette liaison (droit ajouté depuis) : <button class="btn small" type="button" data-google-connect>Relier à nouveau</button></div>`)
          + `<div data-google-check style="margin-top:6px">Vérification de l’accès à l’agenda…</div>`;
        connectBtn.hidden = true; disconnectBtn.hidden = false; testEmailBtn.hidden = !s.canEmail;
        // Contrôle réel, de bout en bout : le statut ne dit « relié » que si l’agenda se lit vraiment.
        const line = box.querySelector('[data-google-check]');
        try {
          const c = await google.check();
          if (!line) return;
          line.innerHTML = c.ok
            ? `${icon('check')} Agenda lisible : ${c.busy ?? 0} période${(c.busy ?? 0) > 1 ? 's' : ''} occupée${(c.busy ?? 0) > 1 ? 's' : ''} dans les 7 prochains jours.`
            : `${icon('alert')} Agenda inaccessible à l’étape « ${esc(c.step ?? '?')} » : ${esc(c.message ?? 'erreur inconnue')}. Déconnectez puis reliez à nouveau.`;
        } catch (e) {
          if (line) line.innerHTML = `${icon('alert')} Contrôle impossible : ${esc(e instanceof Error ? e.message : 'serveur injoignable')}`;
        }
      }
      else { box.textContent = 'Non relié. Google vous demandera votre accord pour lire vos disponibilités et créer des événements.'; connectBtn.hidden = false; disconnectBtn.hidden = true; }
    } catch (e) {
      box.innerHTML = `${icon('alert')} ${esc(e instanceof Error ? e.message : 'Serveur injoignable.')}`;
      connectBtn.hidden = true; disconnectBtn.hidden = true;
    }
  }
  on(main, 'click', '[data-google-connect]', async () => {
    try { location.href = await google.connect(); }
    catch (e) { showToast({ id: 0, kind: 'error', text: e instanceof Error ? e.message : 'Impossible de démarrer la connexion Google.' }); }
  });
  on(main, 'click', '[data-google-disconnect]', async () => {
    if (!confirm('Déconnecter Google Agenda ? Les invitations et les liens Meet ne seront plus créés automatiquement.')) return;
    try { await google.disconnect(); showToast({ id: 0, kind: 'info', text: 'Google Agenda déconnecté.' }); void paintGoogle(); }
    catch (e) { showToast({ id: 0, kind: 'error', text: e instanceof Error ? e.message : 'Déconnexion impossible.' }); }
  });
  on(main, 'click', '[data-google-test-email]', async (_e, t) => {
    const btn = t as HTMLButtonElement;
    btn.disabled = true;
    try {
      const r = await google.testEmail();
      showToast(r.ok
        ? { id: 0, kind: 'info', text: `E-mail de test envoyé à ${r.email || 'votre adresse'}. Regardez votre boîte (et les indésirables).` }
        : { id: 0, kind: 'error', text: `Envoi impossible à l’étape « ${r.step ?? '?'} » : ${r.message ?? 'cause inconnue'}` });
    } catch (e) {
      showToast({ id: 0, kind: 'error', text: e instanceof Error ? e.message : 'Envoi impossible.' });
    } finally { btn.disabled = false; }
  });
  void paintGoogle();

  async function paintPush(): Promise<void> {
    const box = el(main, '[data-push-state]');
    const onBtn = el<HTMLButtonElement>(main, '[data-push-on]');
    const offBtn = el<HTMLButtonElement>(main, '[data-push-off]');
    const s: PushStatus = await pushStatus();
    if (s === 'unsupported') { box.textContent = 'Non disponible sur ce navigateur ou cet appareil. Sur Android, installez l’application depuis Chrome pour les activer.'; onBtn.hidden = true; offBtn.hidden = true; }
    else if (s === 'denied') { box.innerHTML = `${icon('alert')} Autorisation refusée par le navigateur. Réactivez les notifications pour ce site dans ses réglages, puis revenez ici.`; onBtn.hidden = true; offBtn.hidden = true; }
    else if (s === 'on') { box.innerHTML = `${icon('check')} Activées sur cet appareil.`; onBtn.hidden = true; offBtn.hidden = false; }
    else { box.textContent = 'Non activées sur cet appareil.'; onBtn.hidden = false; offBtn.hidden = true; }
  }
  on(main, 'click', '[data-push-on]', async (_e, t) => {
    (t as HTMLButtonElement).disabled = true;
    try { await pushSubscribe(); showToast({ id: 0, kind: 'info', text: 'Notifications activées sur cet appareil.' }); }
    catch (e) { showToast({ id: 0, kind: 'error', text: e instanceof Error ? e.message : 'Activation impossible.' }); }
    finally { (t as HTMLButtonElement).disabled = false; void paintPush(); }
  });
  on(main, 'click', '[data-push-off]', async (_e, t) => {
    (t as HTMLButtonElement).disabled = true;
    try { await pushUnsubscribe(); showToast({ id: 0, kind: 'info', text: 'Notifications désactivées sur cet appareil.' }); }
    catch (e) { showToast({ id: 0, kind: 'error', text: e instanceof Error ? e.message : 'Désactivation impossible.' }); }
    finally { (t as HTMLButtonElement).disabled = false; void paintPush(); }
  });
  void paintPush();

  on(main, 'click', '[data-export]', () => {
    download(`acte5-world-${new Date().toISOString().slice(0, 10)}.json`, store.exportText(), 'application/json;charset=utf-8');
    showToast({ id: 0, kind: 'info', text: 'Sauvegarde téléchargée.' });
  });
  on(main, 'click', '[data-import]', () => ctx.openImport());
  on(main, 'click', '[data-retry]', () => void store.retry());
  on(main, 'click', '[data-logout]', () => { if (confirm('Se déconnecter de cet appareil ? Vos données restent sur le serveur.')) void store.signOut(); });
  on(main, 'click', '[data-restore]', (_e, t) => void store.restoreContact(t.dataset.restore!).then(() => showToast({ id: 0, kind: 'info', text: 'Contact rétabli.' })).catch(() => { /* déjà signalé */ }));
  on(main, 'click', '[data-purge]', (_e, t) => { if (confirm('Supprimer définitivement ce contact ? Cette action est irréversible.')) void store.purgeContact(t.dataset.purge!).catch(() => { /* déjà signalé */ }); });
  on(main, 'click', '[data-empty-trash]', () => { if (confirm('Vider la corbeille ? Cette action est irréversible.')) void store.emptyTrash().catch(() => { /* déjà signalé */ }); });

  el<HTMLFormElement>(main, '[data-profile]').addEventListener('submit', async (e) => {
    e.preventDefault();
    const errorEl = el(main, '[data-error]');
    const saveBtn = el<HTMLButtonElement>(main, '[data-save]');
    errorEl.textContent = '';
    const z = zone.value;
    if (!z) { errorEl.textContent = 'Choisissez votre ville dans la liste.'; zone.focus(); return; }
    saveBtn.disabled = true;
    try {
      await store.saveProfile({
        tz: z.tz, place: z.place, country: z.country,
        start: parseTime(el<HTMLInputElement>(main, '#p-start').value),
        end: parseTime(el<HTMLInputElement>(main, '#p-end').value),
        days,
        displayName: el<HTMLInputElement>(main, '#p-name').value,
        replyEmail: el<HTMLInputElement>(main, '#p-email').value,
        theme,
      });
      showToast({ id: 0, kind: 'info', text: 'Préférences enregistrées.' });
    } catch (err) {
      errorEl.textContent = err instanceof Error ? err.message : 'Enregistrement impossible.';
    } finally { saveBtn.disabled = false; }
  });

  function update(): void {
    const s = store.state;
    el(main, '[data-banner]').innerHTML = syncBanner(store);
    const trash = store.trash;
    el(main, '[data-trash]').innerHTML = trash.length
      ? `<h3 style="font-size:14px;margin-top:6px">Corbeille <span class="muted">· ${trash.length}</span></h3>
        ${trash.map((c) => `<div class="trash-row"><div><b>${esc(label(c))}</b><small>supprimé le ${esc(prettyDate(Date.parse(c.deletedAt!), s.profile.tz))} · effacé automatiquement après 30 jours</small></div>
          <div class="card-actions"><button class="btn small" type="button" data-restore="${esc(c.id)}">${icon('restore')}Rétablir</button><button class="btn small ghost danger" type="button" data-purge="${esc(c.id)}" aria-label="Supprimer définitivement ${esc(label(c))}">${icon('trash')}</button></div></div>`).join('')}
        <div class="card-actions"><button class="btn small ghost danger" type="button" data-empty-trash>Vider la corbeille</button></div>`
      : `<p class="hint">Corbeille vide. Un contact supprimé y reste 30 jours, le temps de changer d’avis.</p>`;
    const st = statusInfo(s.sync, s.dev);
    el(main, '[data-account]').innerHTML = `
      <dt>Compte</dt><dd>${esc(s.user?.email ?? '—')}</dd>
      <dt>État</dt><dd>${esc(st.text)}${st.detail ? ` <span class="muted">· ${esc(st.detail)}</span>` : ''}</dd>
      <dt>Dernière lecture</dt><dd>${s.sync.readAt ? esc(fmtClock(s.sync.readAt)) : '—'}</dd>
      <dt>Version</dt><dd>${esc(versionLabel())}</dd>
      <dt>Construction</dt><dd class="muted">${esc(VERSION.builtAt)} · commit ${esc(VERSION.commit)}</dd>`;
  }

  update();
  return { update };
}

// ── Import ────────────────────────────────────────────────────────────────

let importDialog: HTMLDialogElement | null = null;

export function openImport(store: Store): void {
  if (!importDialog || !document.body.contains(importDialog)) {
    importDialog = document.createElement('dialog');
    importDialog.setAttribute('aria-labelledby', 'importTitle');
    importDialog.innerHTML = `<form method="dialog" novalidate>
      <header class="dlg-head"><div><h2 id="importTitle">Importer une sauvegarde</h2><p>Un fichier JSON exporté par cette application ou par l’ancienne version.</p></div>
        <button class="icon-btn" type="button" data-close aria-label="Fermer">${icon('close')}</button></header>
      <div class="dlg-body">
        <div class="field"><label for="i-file">Fichier</label><input id="i-file" type="file" accept=".json,application/json"></div>
        <div class="field"><label for="i-text">Ou collez le contenu</label><textarea id="i-text" class="textarea" spellcheck="false" placeholder="{ … }"></textarea></div>
        <p class="hint">Les contacts sont ajoutés à votre liste. Les doublons exacts et les exemples de l’ancienne version sont ignorés. Vos préférences ne sont reprises que si vous n’en avez pas encore enregistré.</p>
        <p class="error" role="alert" data-error></p>
      </div>
      <footer class="dlg-foot"><button class="btn" type="button" data-close>Annuler</button><button class="btn primary" type="submit" data-go>${icon('upload')}Importer</button></footer></form>`;
    document.body.append(importDialog);
  }
  const d = importDialog;
  const text = el<HTMLTextAreaElement>(d, '#i-text');
  const file = el<HTMLInputElement>(d, '#i-file');
  const errorEl = el(d, '[data-error]');
  const go = el<HTMLButtonElement>(d, '[data-go]');
  text.value = ''; file.value = ''; errorEl.textContent = ''; go.disabled = false;
  d.querySelectorAll('[data-close]').forEach((b) => { (b as HTMLElement).onclick = () => d.close(); });
  file.onchange = async () => {
    const f = file.files?.[0];
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) { errorEl.textContent = 'Le fichier est trop volumineux (2 Mo maximum).'; file.value = ''; return; }
    try { text.value = await f.text(); errorEl.textContent = ''; } catch { errorEl.textContent = 'Impossible de lire ce fichier.'; }
  };
  el<HTMLFormElement>(d, 'form').onsubmit = async (e) => {
    e.preventDefault();
    errorEl.textContent = '';
    if (!text.value.trim()) { errorEl.textContent = 'Choisissez un fichier ou collez son contenu.'; return; }
    go.disabled = true;
    go.textContent = 'Import en cours…';
    try {
      const r = await store.importText(text.value);
      d.close();
      if (!r.added.length) showToast({ id: 0, kind: 'info', text: r.duplicates ? 'Ces contacts sont déjà dans votre liste.' : 'Aucun contact à importer dans ce fichier.' });
      if (r.skipped || r.demoSkipped) showToast({ id: 0, kind: 'info', text: `${r.skipped ? r.skipped + ' entrée(s) illisible(s) ignorée(s). ' : ''}${r.demoSkipped ? r.demoSkipped + ' exemple(s) de l’ancienne version ignoré(s).' : ''}` });
      if (r.profileImported) showToast({ id: 0, kind: 'info', text: 'Vos préférences ont été reprises de la sauvegarde.' });
    } catch (err) {
      errorEl.textContent = err instanceof Error ? err.message : 'Import impossible.';
    } finally { go.disabled = false; go.innerHTML = `${icon('upload')}Importer`; }
  };
  d.showModal();
}
